import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Edit, Truck, ChevronDown, ChevronUp, Save, X } from 'lucide-react';
import apiClient from '../../utils/api';
import { toast } from 'sonner';

const TransportManagement = () => {
  const [transports, setTransports] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTransport, setEditingTransport] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingEntryData, setEditingEntryData] = useState(null);

  const [formData, setFormData] = useState({ customer_id: '', customer_name: '', entries: [] });
  const [newEntry, setNewEntry] = useState({ transporter_name: '', notes: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [tRes, cRes] = await Promise.all([
        apiClient.get('/transports'),
        apiClient.get('/customers'),
      ]);
      setTransports(tRes.data);
      setCustomers(cRes.data);
    } catch (e) {
      toast.error('Failed to fetch transports');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTransport(null);
    setFormData({ customer_id: '', customer_name: '', entries: [] });
    setNewEntry({ transporter_name: '', notes: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) { toast.error('Please select a customer'); return; }
    try {
      const cust = customers.find(c => c.id === formData.customer_id);
      const custLabel = (cust?.parent_customer_name || cust?.customer_name || cust?.addresses?.[0]?.customer_name_child || formData.customer_name || '').trim();
      const payload = {
        customer_id: formData.customer_id,
        customer_name: custLabel,
        entries: formData.entries,
      };
      if (editingTransport) {
        await apiClient.put(`/transports/${editingTransport.id}`, payload);
        toast.success('Transport updated');
      } else {
        await apiClient.post('/transports', payload);
        toast.success('Transport created');
      }
      setShowModal(false);
      resetForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save transport');
    }
  };

  const handleEdit = (item) => {
    setEditingTransport(item);
    setFormData({
      customer_id: item.customer_id || '',
      customer_name: item.customer_name || '',
      entries: item.entries || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this transport?')) return;
    try {
      await apiClient.delete(`/transports/${id}`);
      toast.success('Transport deleted');
      fetchAll();
    } catch { toast.error('Failed to delete transport'); }
  };

  const addEntry = () => {
    const name = (newEntry.transporter_name || '').trim();
    if (!name) { toast.error('Enter transporter name'); return; }
    setFormData({ ...formData, entries: [...formData.entries, { transporter_name: name, notes: (newEntry.notes || '').trim() || null }] });
    setNewEntry({ transporter_name: '', notes: '' });
  };

  const removeEntry = (idx) => setFormData({ ...formData, entries: formData.entries.filter((_, i) => i !== idx) });

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setEditingEntry(null);
    setEditingEntryData(null);
  };

  const startEditEntry = (rateId, idx, entry) => { setEditingEntry({ rateId, idx }); setEditingEntryData({ ...entry }); };
  const cancelEditEntry = () => { setEditingEntry(null); setEditingEntryData(null); };

  const saveEditEntry = async (item) => {
    if (!editingEntryData || editingEntry === null) return;
    const name = (editingEntryData.transporter_name || '').trim();
    if (!name) { toast.error('Enter transporter name'); return; }
    try {
      const updated = [...(item.entries || [])];
      updated[editingEntry.idx] = { transporter_name: name, notes: (editingEntryData.notes || '').trim() || null };
      await apiClient.put(`/transports/${item.id}`, { entries: updated });
      toast.success('Transporter updated');
      cancelEditEntry();
      fetchAll();
    } catch { toast.error('Failed to update transporter'); }
  };

  const deleteEntryInline = async (item, idx) => {
    if (!window.confirm('Delete this transporter?')) return;
    try {
      const updated = (item.entries || []).filter((_, i) => i !== idx);
      await apiClient.put(`/transports/${item.id}`, { entries: updated });
      toast.success('Transporter deleted');
      fetchAll();
    } catch { toast.error('Failed to delete transporter'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Truck className="h-6 w-6 text-slate-700" />
          <h2 className="text-2xl font-primary font-semibold text-slate-900">Transport Management</h2>
        </div>
        <Button data-testid="add-transport-button" onClick={() => { resetForm(); setShowModal(true); }} className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800">
          <Plus className="h-4 w-4" /> Add Transport
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-600">Loading...</div>
      ) : transports.length === 0 ? (
        <Card className="p-8 text-center">
          <Truck className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-600">No transports yet. Click {'"'}Add Transport{'"'} to create one.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {transports.map(item => (
            <Card key={item.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50" onClick={() => toggleExpand(item.id)}>
                <div className="flex items-center gap-3">
                  {expandedId === item.id ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                  <div>
                    <h3 className="font-semibold text-slate-900">{item.customer_name || '-'}</h3>
                    <p className="text-sm text-slate-500">{item.entries?.length || 0} transporter(s)</p>
                  </div>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button data-testid={`edit-transport-${item.id}`} onClick={() => handleEdit(item)} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"><Edit className="h-4 w-4" /></Button>
                  <Button data-testid={`delete-transport-${item.id}`} onClick={() => handleDelete(item.id)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>

              {expandedId === item.id && (
                <div className="border-t border-slate-200 p-4">
                  {item.entries?.length > 0 ? (
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">Transporter</th>
                          <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4">Notes</th>
                          <th className="text-center text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-4 w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {item.entries.map((entry, idx) => {
                          const isEditing = editingEntry?.rateId === item.id && editingEntry?.idx === idx;
                          return (
                            <tr key={idx} className="border-t border-slate-100">
                              {isEditing ? (
                                <>
                                  <td className="py-2 px-2"><Input value={editingEntryData?.transporter_name || ''} onChange={(e) => setEditingEntryData({ ...editingEntryData, transporter_name: e.target.value })} className="h-8 text-sm" /></td>
                                  <td className="py-2 px-2"><Input value={editingEntryData?.notes || ''} onChange={(e) => setEditingEntryData({ ...editingEntryData, notes: e.target.value })} className="h-8 text-sm" /></td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button variant="ghost" size="sm" onClick={() => saveEditEntry(item)} className="text-green-600 hover:bg-green-50 h-7 w-7 p-0"><Save className="h-4 w-4" /></Button>
                                      <Button variant="ghost" size="sm" onClick={cancelEditEntry} className="text-slate-500 hover:bg-slate-100 h-7 w-7 p-0"><X className="h-4 w-4" /></Button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="py-2 px-4 text-sm font-medium text-slate-900">{entry.transporter_name}</td>
                                  <td className="py-2 px-4 text-sm text-slate-600">{entry.notes || '-'}</td>
                                  <td className="py-2 px-2 text-center">
                                    <div className="flex gap-1 justify-center">
                                      <Button variant="ghost" size="sm" onClick={() => startEditEntry(item.id, idx, entry)} className="text-blue-600 hover:bg-blue-50 h-7 w-7 p-0"><Edit className="h-4 w-4" /></Button>
                                      <Button variant="ghost" size="sm" onClick={() => deleteEntryInline(item, idx)} className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-center text-slate-500 py-4">No transporters yet. Click edit to add some.</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent data-testid="transport-modal" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-primary font-semibold">{editingTransport ? 'Edit Transport' : 'Create New Transport'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label>Customer *</Label>
              <select
                data-testid="transport-customer-select"
                value={formData.customer_id}
                onChange={(e) => {
                  const c = customers.find(cu => cu.id === e.target.value);
                  const label = (c?.parent_customer_name || c?.customer_name || c?.addresses?.[0]?.customer_name_child || '').trim();
                  setFormData({ ...formData, customer_id: e.target.value, customer_name: label });
                }}
                required
                className="mt-2 h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-base"
                disabled={!!editingTransport}
              >
                <option value="">-- Select Customer --</option>
                {[...customers]
                  .map(c => ({ ...c, _label: (c.parent_customer_name || c.customer_name || (c.addresses && c.addresses[0]?.customer_name_child) || '').trim() }))
                  .filter(c => c._label)
                  .sort((a, b) => a._label.localeCompare(b._label))
                  .map(c => <option key={c.id} value={c.id}>{c._label}</option>)}
              </select>
            </div>

            <div>
              <Label className="mb-3 block">Transporters</Label>
              {formData.entries.length > 0 && (
                <div className="border rounded-lg overflow-hidden mb-4">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase py-2 px-3">Transporter</th>
                        <th className="text-left text-xs font-semibold text-slate-500 uppercase py-2 px-3">Notes</th>
                        <th className="text-center text-xs font-semibold text-slate-500 uppercase py-2 px-3 w-16">Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData.entries.map((entry, idx) => (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="py-2 px-3 text-sm font-medium">{entry.transporter_name}</td>
                          <td className="py-2 px-3 text-sm">{entry.notes || '-'}</td>
                          <td className="py-2 px-3 text-center">
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeEntry(idx)} className="text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-3">Add Transporter</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Transporter Name *</Label>
                    <Input data-testid="transport-name-input" value={newEntry.transporter_name} onChange={(e) => setNewEntry({ ...newEntry, transporter_name: e.target.value })} placeholder="e.g. VN Cargo" className="mt-1 h-10" />
                  </div>
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <Input value={newEntry.notes} onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })} placeholder="Optional" className="mt-1 h-10" />
                  </div>
                  <div className="flex items-end">
                    <Button type="button" variant="outline" onClick={addEntry} className="w-full h-10"><Plus className="h-4 w-4 mr-1" />Add</Button>
                  </div>
                </div>
              </div>
            </div>

            <Button data-testid="submit-transport-button" type="submit" disabled={!formData.customer_id} className="w-full bg-slate-900 hover:bg-slate-800 h-12">
              {editingTransport ? 'Update Transport' : 'Create Transport'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportManagement;
